import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { ArrowRight, Check, Phone, FileText, Clock, MessageCircle, Mail, Shield, Globe, Database, Cloud, Cpu, Monitor, Video, Users, HardDrive, Lock, Wifi, Camera, BarChart } from 'lucide-react';
import { saasProducts } from '../data/mock';

const ProductsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const productCategories = [
    {
      id: 'saas',
      name: 'SaaS Products',
      icon: Cloud,
      description: 'Cloud-based software solutions',
      products: saasProducts.map(p => ({
        ...p,
        iconComponent: { Phone, FileText, Clock, MessageCircle, Mail }[p.icon] || Cloud
      }))
    },
    {
      id: 'web',
      name: 'Web Development',
      icon: Globe,
      description: 'Custom web application development',
      products: [
        { name: 'E-commerce Platforms', description: 'Scalable online store solutions with payment integration', features: ['Multi-vendor Support', 'Payment Gateway', 'Inventory Management'] },
        { name: 'Enterprise Portals', description: 'Custom business portals for internal operations', features: ['Role-based Access', 'Dashboard Analytics', 'API Integration'] },
        { name: 'CRM Systems', description: 'Customer relationship management solutions', features: ['Lead Management', 'Sales Pipeline', 'Reporting'] }
      ]
    },
    {
      id: 'mobile',
      name: 'Mobile Apps',
      icon: Monitor,
      description: 'iOS and Android applications',
      products: [
        { name: 'Native iOS Apps', description: 'Swift-based iOS applications', features: ['App Store Ready', 'Push Notifications', 'In-app Purchases'] },
        { name: 'Android Apps', description: 'Kotlin/Java Android applications', features: ['Play Store Ready', 'Material Design', 'Offline Support'] },
        { name: 'Cross-platform Apps', description: 'React Native & Flutter solutions', features: ['Single Codebase', 'Native Performance', 'Hot Reload'] }
      ]
    },
    {
      id: 'ai',
      name: 'AI Tools',
      icon: Cpu,
      description: 'AI-powered automation solutions',
      products: [
        { name: 'Chatbot Development', description: 'Intelligent conversational AI bots', features: ['NLP Integration', 'Multi-channel', 'Analytics'] },
        { name: 'Predictive Analytics', description: 'ML-based business intelligence', features: ['Data Processing', 'Forecasting', 'Visualization'] },
        { name: 'Document Processing', description: 'AI document extraction and analysis', features: ['OCR Technology', 'Data Extraction', 'Validation'] }
      ]
    },
    {
      id: 'cloud',
      name: 'Cloud Solutions',
      icon: Database,
      description: 'Cloud infrastructure services',
      products: [
        { name: 'AWS Solutions', description: 'Amazon Web Services deployment', features: ['EC2, S3, Lambda', 'Auto Scaling', 'Cost Optimization'] },
        { name: 'Google Cloud', description: 'GCP infrastructure setup', features: ['Compute Engine', 'Cloud Functions', 'BigQuery'] },
        { name: 'Data Migration', description: 'Seamless cloud migration services', features: ['Zero Downtime', 'Data Integrity', 'Security'] }
      ]
    }
  ];

  const filteredCategories = activeCategory === 'all' 
    ? productCategories 
    : productCategories.filter(c => c.id === activeCategory);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#B8336A]/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#2D6A6A]/20 blur-3xl" />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <nav className="flex items-center gap-2 text-sm text-gray-400 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white">Products</span>
            </nav>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Empowering-Grade IT &
              <span className="bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] bg-clip-text text-transparent"> Software Products for Modern Business</span>
            </h1>
            <p className="text-gray-300 text-lg mb-8">
              We don't just sell products — we design, develop, deploy, and support them. Our solutions ensure maximum performance, reliability, and long-term value.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-semibold hover:shadow-lg transition-all"
            >
              Talk to a Product Specialist
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setActiveCategory('all')}
              className={`px-5 py-2 rounded-full transition-all ${
                activeCategory === 'all'
                  ? 'bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Products
            </button>
            {productCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-5 py-2 rounded-full transition-all ${
                  activeCategory === cat.id
                    ? 'bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <cat.icon size={16} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          {filteredCategories.map(category => (
            <div key={category.id} className="mb-16">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] flex items-center justify-center text-white">
                  <category.icon size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{category.name}</h2>
                  <p className="text-gray-600">{category.description}</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.products.map((product, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                    
                    {product.features && (
                      <ul className="space-y-2 mb-4">
                        {product.features.map((feature, fIndex) => (
                          <li key={fIndex} className="flex items-center gap-2 text-sm text-gray-600">
                            <Check size={14} className="text-[#2D6A6A]" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    )}

                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-[#B8336A] font-medium text-sm hover:gap-3 transition-all"
                    >
                      Learn More <ArrowRight size={16} />
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f1a]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Let's discuss how our products can help you achieve your business goals.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#B8336A] to-[#2D6A6A] text-white rounded-full font-semibold hover:shadow-lg hover:shadow-[#B8336A]/30 transition-all"
          >
            Schedule a Demo
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProductsPage;
